import { useCallback, useEffect, useState } from "react";

/**
 * Theme handling for the Wardkey console.
 *
 * The actual flip happens in CSS: `:root[data-theme="light"]` overrides the
 * palette RGB triplets defined in `src/styles/globals.css`. This module just
 * resolves the user's preference and writes it to `<html data-theme>` /
 * `<html style="color-scheme">`. An inline script in `index.html` mirrors
 * the same logic so the first paint matches the final theme.
 */

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "wardkey-theme";

const DARK_MEDIA = "(prefers-color-scheme: dark)";

function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia(DARK_MEDIA).matches;
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === "light" || mode === "dark") return mode;
  return prefersDark() ? "dark" : "light";
}

function apply(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const resolved = resolve(mode);
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  root.style.colorScheme = resolved;
  try {
    if (mode === "system") window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    /* localStorage may be unavailable (privacy mode, SSR). The DOM update
       still happened; the preference simply won't persist. */
  }
}

function readStored(): ThemeMode {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* Privacy mode or sandboxed iframe — fall through to system. */
  }
  return "system";
}

export function useTheme() {
  // SSR-safe initial: the inline script in index.html has already set
  // `data-theme` before React mounts, so we only need to know what the user
  // explicitly chose (or "system" if they haven't).
  const [mode, setModeState] = useState<ThemeMode>("system");

  useEffect(() => {
    setModeState(readStored());
  }, []);

  // While following the OS, propagate live theme changes.
  useEffect(() => {
    if (mode !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(DARK_MEDIA);
    const onChange = () => apply("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    apply(next);
  }, []);

  return { mode, setMode, resolved: resolve(mode) };
}
